import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const PendingApprovalPage = () => {
  const navigate = useNavigate();
  const { checkApprovalStatus, logout } = useAuthStore();
  const [isChecking, setIsChecking] = useState(false);

  // Handle return to login - must logout first to avoid redirect loop
  const handleReturnToLogin = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Auto-check approval status every 10 seconds
  useEffect(() => {
    let intervalId;

    const checkStatus = async () => {
      setIsChecking(true);
      const { approved } = await checkApprovalStatus();
      setIsChecking(false);

      if (approved) {
        // User is now approved - redirect to dashboard
        navigate('/app', { replace: true });
      }
    };

    // Check immediately on mount
    checkStatus();

    // Then check every 10 seconds
    intervalId = setInterval(checkStatus, 10000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkApprovalStatus, navigate]);

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="card-dark p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-warning-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-warning-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-100 mb-3">Pending Approval</h1>

          {/* Description */}
          <p className="text-dark-400 mb-4">
            Your account is pending administrator approval. You will receive an email notification
            once your access has been granted.
          </p>

          {/* Auto-check indicator */}
          <div className="flex items-center justify-center gap-2 text-sm text-dark-500 mb-6">
            {isChecking ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                <span>Checking status...</span>
              </>
            ) : (
              <>
                <div className="h-2 w-2 rounded-full bg-primary-500 animate-pulse"></div>
                <span>Auto-checking every 10 seconds</span>
              </>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-dark-900 rounded-lg p-4 mb-6 text-left">
            <h3 className="text-sm font-medium text-dark-300 mb-2">What happens next?</h3>
            <ul className="text-sm text-dark-400 space-y-2">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-400 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>An administrator will review your request</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-400 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>You'll receive an email when approved</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-400 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                <span>Then you can log in and start using MyMDO</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleReturnToLogin}
              className="btn-secondary w-full"
            >
              Return to login
            </button>
            <Link to="/" className="btn-ghost w-full inline-block text-center">
              Back to home
            </Link>
          </div>
        </div>

        {/* Contact */}
        <p className="mt-6 text-center text-dark-500 text-sm">
          Questions? Contact{' '}
          <a href="mailto:support@mymdo.com" className="link-primary">
            support@mymdo.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default PendingApprovalPage;
